import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { toast, Bounce } from "react-toastify";
import {
    fetchPlanManagerHealthEdgeMedicalPlan,
    fetchPlanManagerMedicalPlan,
    fetchPlanManagerSbcUploadUrl,
    fetchPlanManagerSettings,
    fetchPlanManagerSpdUploadUrl,
    fetchPlanManagerUpdateMedicalPlanV2,
    uploadPlanManagerFileToSignedUrl,
} from "../../../../../utils/apiUtils.js";
import { useSelectedPlanStore } from "../../../../../store/selectedPlanStore.js";
import {
    PLAN_MANAGER_QUERY_KEYS,
    PLANS_LIST_ROUTE,
    PlanPlatformTab,
} from "../../../common/constants";
import {
    ACCUMULATORS_STEP_ID,
    annotatePlanSetupStepsWithCompletion,
    buildMedicalPlanV2UpdateBody,
    createEmptyPlanSetupForm,
    DEFAULT_UNGUIDED_TIER_LABEL,
    getPlanSetupNextButtonLabel,
    getVisiblePlanSetupSteps,
    isValidPhoneNumber,
    mapMedicalPlanToSetupForm,
    mapSettingsToSetupOptions,
    parsePlanRouteSearchParams,
    PLAN_DESIGN_VALIDATION_MESSAGES,
    reconcilePlanDesignDatesOnStartChange,
    shouldOpenSubmitConfirmation,
    validateAllPlanSetupSteps,
    validateMemberCostsStep,
    validatePlanSetupStep,
    toggleAllCostTableRows,
} from "../../../../../utils/planManager/planConfigurationUtils.js";

/**
 * Confirmation-modal copy shown when the user submits the wizard.
 * @readonly
 * @type {{ TITLE: string, BODY: string, CONFIRM: string, CANCEL: string }}
 */
export const SUBMIT_MODAL_COPY = Object.freeze({
    TITLE: "Are you sure?",
    BODY: "Submitting will overwrite existing plan configuration.",
    CONFIRM: "Yes, submit",
    CANCEL: "No, cancel",
});

/**
 * Wizard step ids and header labels in display order. The Accumulators step is
 * dropped for HRP plans by `getVisiblePlanSetupSteps`.
 * @readonly
 * @type {ReadonlyArray<{ id: string, label: string }>}
 */
export const PLAN_SETUP_STEPS = Object.freeze([
    { id: "planDesign", label: "Plan Design" },
    { id: "vendorDetails", label: "Vendor Details" },
    { id: "memberCosts", label: "Member Costs" },
    { id: ACCUMULATORS_STEP_ID, label: "Accumulators" },
    { id: "setup", label: "Setup" },
]);

/**
 * Toast messages for verify and medical-plan v2 submit outcomes.
 * @readonly
 * @type {{ VERIFY: string, SUBMIT: string, SAVE_ERROR: string }}
 */
export const PLAN_SETUP_TOAST_MESSAGES = Object.freeze({
    VERIFY: "Plan ready to be submitted!",
    SUBMIT: "Plan submitted successfully.",
    SAVE_ERROR: "Failed to submit plan. Please try again.",
});

/**
 * Header and copy for the review-before-submitting warning banner.
 * @readonly
 */
export const PLAN_SETUP_REVIEW_BANNER = Object.freeze({
    TITLE: "Review before submitting",
    BODY: "Review all fields prior to submitting to ensure input fields reflect the correct information.",
});

/**
 * Inline ActionBar alerts for validation failures.
 * @readonly
 * @type {{ NEXT: string, VERIFY: string }}
 */
export const PLAN_SETUP_VALIDATION_ALERTS = Object.freeze({
    NEXT: "Complete the required fields before continuing.",
    VERIFY: "Fix incomplete steps before verifying the plan.",
});

/**
 * Shared `react-toastify` options for stub action toasts on this screen.
 * @type {import("react-toastify").ToastOptions}
 */
const PLAN_SETUP_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});

const PLAN_SETUP_LOAD_ERROR_MESSAGE =
    "Plan setup data is currently unavailable. Please try again later.";

/**
 * Error shown on the Setup step when an SPD/SBC upload fails.
 * @constant {string}
 */
export const PLAN_DOCUMENT_UPLOAD_ERROR =
    "Failed to upload document. Please try again.";

/**
 * MIME type sent to the signed-URL endpoints and on the S3 PUT. The two must
 * match or S3 rejects the upload signature.
 * @constant {string}
 */
export const PLAN_DOCUMENT_CONTENT_TYPE = "application/pdf";

const SIGNED_UPLOAD_URL_FETCHERS = Object.freeze({
    sbc: fetchPlanManagerSbcUploadUrl,
    spd: fetchPlanManagerSpdUploadUrl,
});

/**
 * Builds the object-key base name for a plan document. The API appends the
 * extension from the MIME type, so the local `.pdf` is stripped; client/plan
 * ids are prefixed so same-named files on different plans don't collide.
 * @param {{ clientId?: string, planId?: string, fileName: string }} params
 * @returns {string}
 */
export const buildPlanDocumentUploadName = ({ clientId, planId, fileName }) => {
    const baseName = String(fileName ?? "").replace(/\.pdf$/i, "");
    return [clientId, planId, baseName].filter(Boolean).join("-");
};

/**
 * Ephemeral wizard state and handlers for the plan setup screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function useSetup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();

    const storeClientId = useSelectedPlanStore((state) => state.clientId);
    const storePlanId = useSelectedPlanStore((state) => state.planId);
    const storePlatformTab = useSelectedPlanStore((state) => state.platformTab);
    const importedPlanData = useSelectedPlanStore(
        (state) => state.importedPlanData,
    );
    const isReview = useSelectedPlanStore((state) => state.isReview);
    const clearSelectedPlan = useSelectedPlanStore(
        (state) => state.clearSelectedPlan,
    );

    const parsedParams = useMemo(
        () => parsePlanRouteSearchParams(searchParams),
        [searchParams],
    );

    const clientId = storeClientId || parsedParams.clientId;
    const planId = storePlanId || parsedParams.planId;
    const platformTab =
        storePlatformTab ||
        parsedParams.platformTab ||
        (planId?.startsWith("HRP_")
            ? PlanPlatformTab.HRP
            : PlanPlatformTab.JAVELINA);

    const isHrp = platformTab === PlanPlatformTab.HRP;

    const [form, setForm] = useState(createEmptyPlanSetupForm);
    const [activeStep, setActiveStep] = useState(1);
    const [evaluatedStepIds, setEvaluatedStepIds] = useState(() => new Set());
    const [hasVerified, setHasVerified] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [memberCostsFieldErrors, setMemberCostsFieldErrors] = useState({
        unguidedTierLabelError: "",
    });
    const [stepErrors, setStepErrors] = useState({});
    const [validationAlert, setValidationAlert] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingDocType, setUploadingDocType] = useState(null);
    const nextRowIdRef = useRef(0);
    const submitLockRef = useRef(false);

    useEffect(() => {
        if (!clientId || !planId) {
            navigate(PLANS_LIST_ROUTE, { replace: true });
        }
    }, [clientId, planId, navigate]);

    const settingsQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.settings,
        queryFn: fetchPlanManagerSettings,
    });

    const medicalPlanQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.medicalPlan(
            clientId,
            planId,
            platformTab,
        ),
        queryFn: () =>
            isHrp
                ? fetchPlanManagerHealthEdgeMedicalPlan(clientId, planId)
                : fetchPlanManagerMedicalPlan(clientId, planId),
        enabled: Boolean(clientId && planId && !importedPlanData),
    });

    const options = useMemo(
        () => mapSettingsToSetupOptions(settingsQuery.data ?? {}),
        [settingsQuery.data],
    );

    useEffect(() => {
        if (importedPlanData) {
            setForm(mapMedicalPlanToSetupForm(importedPlanData, isHrp));
            setHasVerified(false);
            setStepErrors({});
            setValidationAlert("");
            setEvaluatedStepIds(new Set());
            return;
        }

        if (!medicalPlanQuery.data) {
            return;
        }

        setForm(mapMedicalPlanToSetupForm(medicalPlanQuery.data, isHrp));
        setHasVerified(false);
        setStepErrors({});
        setValidationAlert("");
        setEvaluatedStepIds(new Set());
    }, [importedPlanData, medicalPlanQuery.data, isHrp]);

    const visibleSteps = useMemo(
        () => getVisiblePlanSetupSteps(PLAN_SETUP_STEPS, isHrp),
        [isHrp],
    );
    const stepperSteps = useMemo(
        () =>
            annotatePlanSetupStepsWithCompletion(
                visibleSteps,
                form,
                evaluatedStepIds,
            ),
        [visibleSteps, form, evaluatedStepIds],
    );
    const totalSteps = visibleSteps.length;
    const currentStep = visibleSteps[activeStep - 1] ?? visibleSteps[0];
    const nextLabel = getPlanSetupNextButtonLabel({
        activeStep,
        totalSteps,
        hasVerified,
    });

    const isLoading =
        settingsQuery.isLoading ||
        (Boolean(clientId && planId) && medicalPlanQuery.isLoading);
    const isError = settingsQuery.isError || medicalPlanQuery.isError;
    const loadErrorMessage =
        settingsQuery.error?.message ??
        medicalPlanQuery.error?.message ??
        PLAN_SETUP_LOAD_ERROR_MESSAGE;

    const clearValidationState = () => {
        setStepErrors({});
        setValidationAlert("");
    };

    const markStepEvaluated = (stepId) => {
        if (!stepId) return;
        setEvaluatedStepIds((previous) => {
            if (previous.has(stepId)) return previous;
            const next = new Set(previous);
            next.add(stepId);
            return next;
        });
    };

    const handleFieldChange = (stepKey) => (field, value) => {
        markStepEvaluated(stepKey);

        if (stepKey === "planDesign" && field === "startDate") {
            setForm((previous) => ({
                ...previous,
                planDesign: reconcilePlanDesignDatesOnStartChange(
                    previous.planDesign,
                    value,
                ),
            }));
            setStepErrors((previous) => {
                if (!previous.endDate && !previous.startDate) {
                    return previous;
                }
                const next = { ...previous };
                delete next.startDate;
                delete next.endDate;
                return next;
            });
            setHasVerified(false);
            return;
        }

        setForm((previous) => ({
            ...previous,
            [stepKey]: { ...previous[stepKey], [field]: value },
        }));
        if (stepKey === "planDesign" && field === "contactNumber") {
            if (isValidPhoneNumber(value)) {
                setStepErrors((previous) => {
                    if (!previous.contactNumber) {
                        return previous;
                    }
                    const next = { ...previous };
                    delete next.contactNumber;
                    return next;
                });
            }
        }
        if (stepKey === "memberCosts") {
            setMemberCostsFieldErrors({ unguidedTierLabelError: "" });
        }
        setHasVerified(false);
        setStepErrors((previous) => {
            if (!previous[field]) {
                return previous;
            }

            const nextErrors = { ...previous };
            delete nextErrors[field];
            return nextErrors;
        });
        setValidationAlert("");
    };

    const handleFieldBlur = (stepKey) => (field) => {
        if (stepKey === "planDesign" && field === "contactNumber") {
            const contactNumber = form.planDesign?.contactNumber;
            if (!contactNumber || !isValidPhoneNumber(contactNumber)) {
                setStepErrors((previous) => ({
                    ...previous,
                    contactNumber:
                        PLAN_DESIGN_VALIDATION_MESSAGES.CONTACT_NUMBER,
                }));
            } else {
                setStepErrors((previous) => {
                    if (!previous.contactNumber) {
                        return previous;
                    }
                    const next = { ...previous };
                    delete next.contactNumber;
                    return next;
                });
            }
        }
    };

    const handleIncludeUnguidedColumnChange = (checked) => {
        markStepEvaluated("memberCosts");
        setForm((previous) => {
            const previousMemberCosts = previous.memberCosts ?? {};
            let nextMemberCosts = {
                ...previousMemberCosts,
                includeUnguidedColumn: checked,
            };

            if (checked) {
                const unguidedTierLabel = String(
                    previousMemberCosts.unguidedTierLabel ?? "",
                ).trim()
                    ? previousMemberCosts.unguidedTierLabel
                    : DEFAULT_UNGUIDED_TIER_LABEL;
                const tier1 =
                    previousMemberCosts.tier1 === "In-network"
                        ? "Guided (In-Network)"
                        : previousMemberCosts.tier1;

                nextMemberCosts = {
                    ...nextMemberCosts,
                    unguidedTierLabel,
                    tier1,
                };
            } else {
                nextMemberCosts = {
                    ...nextMemberCosts,
                    medicalRows: (previousMemberCosts.medicalRows ?? []).map(
                        (row) => ({
                            ...row,
                            unguidedInNetwork: "",
                        }),
                    ),
                };
            }

            return {
                ...previous,
                memberCosts: nextMemberCosts,
            };
        });
        setMemberCostsFieldErrors({ unguidedTierLabelError: "" });
        setHasVerified(false);
        setValidationAlert("");
    };

    const setSetupDocument = (docType, fileName, url) => {
        setForm((previous) => ({
            ...previous,
            setup: {
                ...previous.setup,
                [`${docType}FileName`]: fileName,
                [`${docType}Url`]: url,
            },
        }));
        setHasVerified(false);
        setValidationAlert("");
    };

    /**
     * Uploads an SPD/SBC PDF via a signed S3 URL from the plan-manager API and
     * records the resulting object URL on the setup form.
     * @param {"spd" | "sbc"} docType
     * @param {File} file
     * @returns {Promise<void>} Rejects with a user-facing message on failure.
     */
    const handleUploadDocument = async (docType, file) => {
        const fetchSignedUrl = SIGNED_UPLOAD_URL_FETCHERS[docType];
        if (!fetchSignedUrl || !file) {
            throw new Error(PLAN_DOCUMENT_UPLOAD_ERROR);
        }

        setUploadingDocType(docType);
        try {
            const result = await fetchSignedUrl({
                fileName: buildPlanDocumentUploadName({
                    clientId,
                    planId,
                    fileName: file.name,
                }),
                fileType: PLAN_DOCUMENT_CONTENT_TYPE,
            });

            if (!result?.signedUrl || !result?.publicObjectUrl) {
                throw new Error(PLAN_DOCUMENT_UPLOAD_ERROR);
            }

            await uploadPlanManagerFileToSignedUrl(
                result.signedUrl,
                file,
                PLAN_DOCUMENT_CONTENT_TYPE,
            );
            setSetupDocument(docType, file.name, result.publicObjectUrl);
        } catch {
            throw new Error(PLAN_DOCUMENT_UPLOAD_ERROR);
        } finally {
            setUploadingDocType(null);
        }
    };

    const handleRemoveDocument = (docType) => {
        markStepEvaluated("setup");
        setSetupDocument(docType, "", "");
    };

    const handleRowChange = (tableKey, rowId, field, value) => {
        markStepEvaluated("memberCosts");
        setForm((previous) => ({
            ...previous,
            memberCosts: {
                ...previous.memberCosts,
                [tableKey]: previous.memberCosts[tableKey].map((row) =>
                    row.id === rowId ? { ...row, [field]: value } : row,
                ),
            },
        }));
        setHasVerified(false);
        setValidationAlert("");
    };

    const handleRowToggle = (tableKey, rowId) => {
        markStepEvaluated("memberCosts");
        setForm((previous) => ({
            ...previous,
            memberCosts: {
                ...previous.memberCosts,
                [tableKey]: previous.memberCosts[tableKey].map((row) =>
                    row.id === rowId
                        ? { ...row, isChecked: !row.isChecked }
                        : row,
                ),
            },
        }));
    };

    const handleToggleAllRows = (tableKey) => {
        markStepEvaluated("memberCosts");
        setForm((previous) => ({
            ...previous,
            memberCosts: {
                ...previous.memberCosts,
                [tableKey]: toggleAllCostTableRows(
                    previous.memberCosts?.[tableKey],
                ),
            },
        }));
        setHasVerified(false);
        setValidationAlert("");
    };

    const handleAddRow = (tableKey) => {
        markStepEvaluated("memberCosts");
        nextRowIdRef.current += 1;
        const newRow = {
            id: `new-${tableKey}-${nextRowIdRef.current}`,
            category: "",
            inNetwork: "",
            unguidedInNetwork: "",
            outOfNetwork: "",
            userInput1: "",
            userInput2: "",
            isChecked: false,
        };
        setForm((previous) => ({
            ...previous,
            memberCosts: {
                ...previous.memberCosts,
                [tableKey]: [...previous.memberCosts[tableKey], newRow],
            },
        }));
    };

    const handleResetAccumulators = () => {
        markStepEvaluated("accumulators");
        setForm((previous) => ({
            ...previous,
            accumulators: {
                ...previous.accumulators,
                category: "",
                planYear: "",
                deductibleType: "",
                inNetworkLabel: "",
                outOfNetworkLabel: "",
                inNetworkDeductiblePatientAmountEnabled: false,
                outOfNetworkDeductiblePatientAmountEnabled: false,
                inNetworkDeductibleFamilyAmountEnabled: false,
                outOfNetworkDeductibleFamilyAmountEnabled: false,
                inNetworkOutOfPocketPatientMaxAmountEnabled: false,
                outOfNetworkOutOfPocketPatientMaxAmountEnabled: false,
                inNetworkOutOfPocketFamilyMaxAmountEnabled: false,
                outOfNetworkOutOfPocketFamilyMaxAmountEnabled: false,
            },
        }));
        setHasVerified(false);
        clearValidationState();
    };

    const handleStepJump = (stepIndex) => {
        if (currentStep?.id) {
            markStepEvaluated(currentStep.id);
        }
        setActiveStep(stepIndex + 1);
        setHasVerified(false);
        clearValidationState();
    };

    const handlePrevious = () => {
        if (currentStep?.id) {
            markStepEvaluated(currentStep.id);
        }
        setActiveStep((previous) => Math.max(1, previous - 1));
        setHasVerified(false);
        clearValidationState();
    };

    const handleNext = () => {
        if (currentStep?.id) {
            markStepEvaluated(currentStep.id);
        }

        if (currentStep?.id === "memberCosts") {
            const validation = validateMemberCostsStep(form.memberCosts);
            if (!validation.isValid) {
                setStepErrors(validation.errors);
                setMemberCostsFieldErrors({
                    unguidedTierLabelError: validation.unguidedTierLabelError,
                });
                setValidationAlert(PLAN_SETUP_VALIDATION_ALERTS.NEXT);
                return;
            }
            setMemberCostsFieldErrors({ unguidedTierLabelError: "" });
        }

        if (activeStep < totalSteps) {
            const stepValidation = validatePlanSetupStep(currentStep?.id, form);

            if (!stepValidation.isValid) {
                setStepErrors(stepValidation.errors);
                setValidationAlert(PLAN_SETUP_VALIDATION_ALERTS.NEXT);
                return;
            }

            clearValidationState();
            setActiveStep((previous) => previous + 1);
            return;
        }

        if (shouldOpenSubmitConfirmation({ activeStep, totalSteps, hasVerified })) {
            setIsConfirmModalOpen(true);
            return;
        }

        setEvaluatedStepIds(new Set(visibleSteps.map((step) => step.id)));

        const allStepsValidation = validateAllPlanSetupSteps(visibleSteps, form);

        if (!allStepsValidation.isValid) {
            const invalidStepId = allStepsValidation.firstInvalidStepId;
            const invalidErrors =
                allStepsValidation.stepResults[invalidStepId]?.errors ?? {};

            setStepErrors(invalidErrors);
            setValidationAlert(PLAN_SETUP_VALIDATION_ALERTS.VERIFY);
            setActiveStep(allStepsValidation.firstInvalidStepIndex + 1);
            setHasVerified(false);
            return;
        }

        clearValidationState();
        setHasVerified(true);
        toast.info(PLAN_SETUP_TOAST_MESSAGES.VERIFY, PLAN_SETUP_TOAST_OPTIONS);
    };

    const handleConfirmSubmit = async () => {
        if (submitLockRef.current) {
            return;
        }

        if (!clientId || !planId) {
            setIsConfirmModalOpen(false);
            toast.error(
                PLAN_SETUP_TOAST_MESSAGES.SAVE_ERROR,
                PLAN_SETUP_TOAST_OPTIONS,
            );
            return;
        }

        submitLockRef.current = true;
        setIsSubmitting(true);
        setIsConfirmModalOpen(false);

        const body = buildMedicalPlanV2UpdateBody({
            form,
            clientId,
            planId,
            isHrp,
        });

        try {
            const result = await fetchPlanManagerUpdateMedicalPlanV2(body);
            if (result == null) {
                toast.error(
                    PLAN_SETUP_TOAST_MESSAGES.SAVE_ERROR,
                    PLAN_SETUP_TOAST_OPTIONS,
                );
                return;
            }

            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.medicalPlan(
                    clientId,
                    planId,
                    platformTab,
                ),
            });
            await queryClient.invalidateQueries({
                queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
            });
            toast.success(
                PLAN_SETUP_TOAST_MESSAGES.SUBMIT,
                PLAN_SETUP_TOAST_OPTIONS,
            );
            clearSelectedPlan();
            navigate(PLANS_LIST_ROUTE);
        } catch {
            toast.error(
                PLAN_SETUP_TOAST_MESSAGES.SAVE_ERROR,
                PLAN_SETUP_TOAST_OPTIONS,
            );
        } finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleCloseConfirmModal = () => setIsConfirmModalOpen(false);

    return {
        form,
        activeStep,
        isHrp,
        isReview,
        visibleSteps: stepperSteps,
        currentStep,
        nextLabel,
        hasVerified,
        isConfirmModalOpen,
        stepErrors,
        validationAlert,
        options,
        planId,
        isLoading,
        isError,
        isSubmitting,
        uploadingDocType,
        loadErrorMessage,
        memberCostsFieldErrors,
        handleFieldChange,
        handleFieldBlur,
        handleIncludeUnguidedColumnChange,
        handleUploadDocument,
        handleRemoveDocument,
        handleRowChange,
        handleRowToggle,
        handleToggleAllRows,
        handleAddRow,
        handleResetAccumulators,
        handleStepJump,
        handlePrevious,
        handleNext,
        handleConfirmSubmit,
        handleCloseConfirmModal,
    };
}
